import React from 'react';
import { Button } from '../components/ui/Button';
import { Card, CardHeader, CardBody, CardFooter } from '../components/ui/Card';
import { CtaSection } from '../components/sections/CtaSection';
import { CalendarIcon, DownloadIcon, ClockIcon, TagIcon, ArrowRightIcon } from 'lucide-react';
export function InsightHub() {
  const blogPosts = [{
    title: 'How to Measure Retreat ROI',
    excerpt: 'Learn practical methods to quantify the return on investment from your corporate retreats beyond just team satisfaction.',
    date: 'October 15, 2023',
    readTime: '8 min read',
    category: 'Strategy',
    image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80'
  }, {
    title: 'Cancun vs. Miami: MICE Showdown',
    excerpt: 'A detailed comparison of hosting your next corporate event in Cancun versus Miami, from budget considerations to unique experiences.',
    date: 'September 28, 2023',
    readTime: '10 min read',
    category: 'Destinations',
    image: 'https://images.unsplash.com/photo-1602088113235-229c19758e9f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80'
  }, {
    title: 'Cancun vs. Vegas: The Ultimate Corporate Destination Battle',
    excerpt: 'Which destination offers better value, experiences, and outcomes for your next corporate event? We break down the pros and cons.',
    date: 'August 17, 2023',
    readTime: '12 min read',
    category: 'Destinations',
    image: 'https://images.unsplash.com/photo-1605833556294-ea5c7a74f57d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2074&q=80'
  }, {
    title: 'The 2025 Corporate Retreat Budget Guide',
    excerpt: 'Plan ahead with our comprehensive guide to budgeting for corporate retreats in 2025, including emerging trends and cost-saving strategies.',
    date: 'November 5, 2023',
    readTime: '15 min read',
    category: 'Planning',
    image: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2011&q=80'
  }];
  const resources = [{
    title: 'Corporate Retreat Budget Calculator',
    type: 'Calculator',
    description: 'Interactive tool to estimate costs based on group size, duration, and activities.',
    image: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2011&q=80'
  }, {
    title: 'Event Planning Checklist',
    type: 'PDF Guide',
    description: 'Comprehensive checklist covering all aspects of planning a successful corporate event.',
    image: 'https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80'
  }, {
    title: 'ROI Measurement Framework',
    type: 'Template',
    description: 'Excel template for tracking and measuring the ROI of your corporate retreats.',
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2015&q=80'
  }];
  return <div className="pt-20 bg-[#F4E8D0]/30" data-id="element-2323">
      {/* Hero */}
      <section className="bg-[#0099A1] py-20" data-id="element-2324">
        <div className="container mx-auto px-4 md:px-6" data-id="element-2325">
          <div className="max-w-3xl" data-id="element-2326">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4" data-id="element-2327">
              Insight Hub
            </h1>
            <p className="text-xl text-white/90" data-id="element-2328">
              Expert resources to help you plan and maximize the impact of your
              corporate events.
            </p>
          </div>
        </div>
      </section>
      {/* Webinar Banner */}
      <section className="py-8 bg-[#FF6B65]" data-id="element-2329">
        <div className="container mx-auto px-4 md:px-6" data-id="element-2330">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6" data-id="element-2331">
            <div className="text-white" data-id="element-2332">
              <h2 className="text-2xl font-bold mb-2" data-id="element-2333">
                Retreat ROI Live Webinar
              </h2>
              <p data-id="element-2334">
                Join our experts on November 6, 2025 to learn how to measure and
                maximize your retreat ROI.
              </p>
            </div>
            <Button variant="primary" className="bg-[#374047] text-white hover:bg-[#374047]/90" data-id="element-2335">
              <CalendarIcon className="h-5 w-5 mr-2" data-id="element-2336" />
              Register Now
            </Button>
          </div>
        </div>
      </section>
      {/* Blog Posts */}
      <section className="py-16 bg-white" data-id="element-2337">
        <div className="container mx-auto px-4 md:px-6" data-id="element-2338">
          <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-2339">
            <h2 className="text-3xl font-bold text-[#374047] mb-4" data-id="element-2340">
              Latest Insights
            </h2>
            <p className="text-lg text-[#374047]/70" data-id="element-2341">
              Expert articles to help you plan and execute exceptional corporate
              events.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-2342">
            {blogPosts.map((post, index) => <Card key={index} className="h-full overflow-hidden" data-id="element-2343">
                <div className="h-48 overflow-hidden" data-id="element-2344">
                  <img src={post.image} alt={post.title} className="w-full h-full object-cover" data-id="element-2345" />
                </div>
                <CardHeader data-id="element-2346">
                  <div className="flex justify-between items-center mb-2" data-id="element-2347">
                    <div className="flex items-center" data-id="element-2348">
                      <CalendarIcon className="h-4 w-4 text-[#0099A1] mr-2" data-id="element-2349" />
                      <span className="text-xs text-[#374047]/60" data-id="element-2350">
                        {post.date}
                      </span>
                    </div>
                    <div className="flex items-center" data-id="element-2351">
                      <ClockIcon className="h-4 w-4 text-[#0099A1] mr-2" data-id="element-2352" />
                      <span className="text-xs text-[#374047]/60" data-id="element-2353">
                        {post.readTime}
                      </span>
                    </div>
                  </div>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#0099A1]/10 text-[#0099A1] mb-2" data-id="element-2354">
                    <TagIcon className="h-3 w-3 mr-1" data-id="element-2355" />
                    {post.category}
                  </span>
                  <h3 className="text-xl font-bold text-[#374047]" data-id="element-2356">
                    {post.title}
                  </h3>
                </CardHeader>
                <CardBody data-id="element-2357">
                  <p className="text-[#374047]/80" data-id="element-2358">{post.excerpt}</p>
                </CardBody>
                <CardFooter data-id="element-2359">
                  <Button variant="text" className="text-[#0099A1] hover:text-[#0099A1]/80" data-id="element-2360">
                    Read More
                    <ArrowRightIcon className="ml-2 h-4 w-4" data-id="element-2361" />
                  </Button>
                </CardFooter>
              </Card>)}
          </div>
        </div>
      </section>
      {/* Resources */}
      <section className="py-16 bg-[#F4E8D0]/50" data-id="element-2362">
        <div className="container mx-auto px-4 md:px-6" data-id="element-2363">
          <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-2364">
            <h2 className="text-3xl font-bold text-[#374047] mb-4" data-id="element-2365">
              Free Resources
            </h2>
            <p className="text-lg text-[#374047]/70" data-id="element-2366">
              Download our most popular planning tools and templates.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-2367">
            {resources.map((resource, index) => <Card key={index} className="h-full" data-id="element-2368">
                <div className="h-40 overflow-hidden" data-id="element-2369">
                  <img src={resource.image} alt={resource.title} className="w-full h-full object-cover" data-id="element-2370" />
                </div>
                <CardHeader data-id="element-2371">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#0099A1]/10 text-[#0099A1] mb-2" data-id="element-2372">
                    {resource.type}
                  </span>
                  <h3 className="text-lg font-bold text-[#374047]" data-id="element-2373">
                    {resource.title}
                  </h3>
                </CardHeader>
                <CardBody data-id="element-2374">
                  <p className="text-sm text-[#374047]/80" data-id="element-2375">
                    {resource.description}
                  </p>
                </CardBody>
                <CardFooter data-id="element-2376">
                  <Button variant="outline" className="w-full flex items-center justify-center" data-id="element-2377">
                    <DownloadIcon className="h-4 w-4 mr-2" data-id="element-2378" />
                    Download
                  </Button>
                </CardFooter>
              </Card>)}
          </div>
        </div>
      </section>
      <CtaSection data-id="element-2379" />
    </div>;
}